import React, { useRef, type PropsWithChildren, memo } from 'react';
import { type RefetchFn, graphql, useFragment } from 'react-relay';
import type { GraphQLTaggedNode } from 'relay-runtime';
import { fg } from '@atlassian/jira-feature-gating';
import type { FilterRefetchQuery } from '@atlassian/jira-relay/src/__generated__/FilterRefetchQuery.graphql';

import type { IssueSearchInputProvider_jiraFilter$key } from '@atlassian/jira-relay/src/__generated__/IssueSearchInputProvider_jiraFilter.graphql';
import { FilterTypes, SearchInputTypes } from '../../common/constants.tsx';
import type { FilterId, FilterJql, SearchInputType } from '../../common/types.tsx';
import { ActiveJqlProvider } from '../../services/active-jql/index.tsx';
import { FilterQueryProvider } from '../../services/filter-query/index.tsx';

const getFilterFromSearchInput = (
	searchParam: SearchInputType,
): FilterJql | FilterId | undefined => {
	if (searchParam?.type === SearchInputTypes.JQL) {
		return undefined;
	}
	return searchParam.type === SearchInputTypes.FILTER_AND_JQL ? searchParam.filter : searchParam;
};

type Props = PropsWithChildren<{
	filterQuery: GraphQLTaggedNode;
	jiraFilter: IssueSearchInputProvider_jiraFilter$key | null;
	refetch: RefetchFn<FilterRefetchQuery>;
	searchInput: SearchInputType;
}>;

export const IssueSearchInputProvider = memo(
	({ children, filterQuery, refetch, searchInput, jiraFilter }: Props) => {
		const filterData = useFragment<IssueSearchInputProvider_jiraFilter$key>(
			graphql`
				fragment IssueSearchInputProvider_jiraFilter on JiraFilter {
					jql
					... on JiraCustomFilter {
						isEditable
					}
				}
			`,
			jiraFilter,
		);

		const filterInput = getFilterFromSearchInput(searchInput);

		const filterId = filterInput?.type === FilterTypes.ID ? filterInput?.value : undefined;
		const filterJql = filterInput?.type === FilterTypes.JQL ? filterInput?.value : filterData?.jql;
		const isFilterEditable = filterData?.isEditable;

		let jql;
		if (
			searchInput?.type === SearchInputTypes.FILTER_AND_JQL ||
			searchInput?.type === SearchInputTypes.JQL
		) {
			jql = searchInput?.jql;
		}

		const initialJql = useRef(
			fg('jracloud_85520_hydration_ref_only_on_page_load') ? jql ?? filterJql : undefined,
		);

		return (
			<FilterQueryProvider query={filterQuery} refetch={refetch} filterId={filterId}>
				<ActiveJqlProvider
					initialJql={initialJql.current}
					filterId={filterId}
					filterJql={filterJql}
					jql={jql}
					isFilterEditable={isFilterEditable ?? undefined}
				>
					{children}
				</ActiveJqlProvider>
			</FilterQueryProvider>
		);
	},
);
